import { SystemStyleObject } from '@chakra-ui/react'

import { tokenVar } from '../../theme/tokenVar'
import type { ColorScheme } from '../../types'
import type {
  SegmentedControlOptionState,
  SegmentedControlVariant,
} from './SegmentedControl'

export type SegmentedControlDotTone = 'notification' | 'error'

const tokens = {
  dark: {
    trackBg: 'bg.segmented-track',
    segmentHoverBg: 'bg.segment-hover',
    activeBg: 'bg.segment-active',
    activeBorder: 'interactive.tertiary-active-border',
    activeText: 'text.primary',
    defaultText: 'text.secondary',
    disabledText: 'text.tertiary',
    spinnerBorder: 'text.tertiary',
    spinnerArc: 'text.secondary',
    notificationDot: 'bg.notification-dot',
    errorDot: 'status.error',
    errorIcon: 'colors.status.error',
  },
  light: {
    trackBg: 'light.bg.segmented-track',
    segmentHoverBg: 'light.bg.segment-hover',
    activeBg: 'light.bg.segment-active',
    activeBorder: 'interactive.tertiary-active-border',
    activeText: 'light.text.primary',
    defaultText: 'light.text.secondary',
    disabledText: 'light.text.tertiary',
    spinnerBorder: 'light.text.tertiary',
    spinnerArc: 'light.text.secondary',
    notificationDot: 'light.bg.notification-dot',
    errorDot: 'light.status.error',
    errorIcon: 'colors.light.status.error',
  },
} as const

const accentTokens = {
  dark: {
    activeBg: 'interactive.primary',
    activeText: 'text.on-accent',
  },
  light: {
    activeBg: 'light.interactive.primary',
    activeText: 'light.text.on-accent',
  },
} as const

/**
 * The error glyph is an svg taking a `color` string, not a Chakra style prop, so it needs the
 * resolved CSS var rather than a token path.
 */
export const errorIconColor = (colorScheme: ColorScheme = 'dark'): string =>
  tokenVar(tokens[colorScheme].errorIcon)

export const trackStyles = (
  colorScheme: ColorScheme = 'dark',
  variant: SegmentedControlVariant = 'default'
): SystemStyleObject => ({
  bg: tokens[colorScheme].trackBg,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  padding: variant === 'accent' ? 0 : '4px',
})

export const segmentStyles = (
  isActive: boolean,
  colorScheme: ColorScheme = 'dark',
  state: SegmentedControlOptionState = 'ready',
  hasTooltip = false,
  variant: SegmentedControlVariant = 'default'
): SystemStyleObject => {
  const isDisabled = state === 'disabled'
  const isLoading = state === 'loading'
  const isError = state === 'error'
  const isInteractable = !isActive && !isDisabled && !isLoading && !isError
  const isAccent = variant === 'accent'
  const activeBg = isAccent
    ? accentTokens[colorScheme].activeBg
    : tokens[colorScheme].activeBg
  const activeText = isAccent
    ? accentTokens[colorScheme].activeText
    : tokens[colorScheme].activeText

  return {
    display: 'flex',
    flex: '1 0 0',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    px: '16px',
    position: 'relative',
    cursor: isInteractable ? 'pointer' : 'default',
    userSelect: 'none',
    // An errored segment keeps full opacity — its label is muted via `defaultText` below, and
    // dimming it would wash out the red alert glyph the state exists to show.
    opacity: isDisabled ? 0.5 : 1,
    // A segment carrying a tooltip must stay a hover target even when it isn't clickable,
    // otherwise its `onMouseEnter` never fires. Safe because the hover *styles* and the click
    // handler are gated on `isInteractable` independently.
    pointerEvents: isInteractable || hasTooltip ? 'auto' : 'none',
    transition:
      'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
    ...(isActive
      ? {
          bg: activeBg,
          border: isAccent ? 'none' : '1px solid',
          ...(!isAccent && {
            borderColor: tokens[colorScheme].activeBorder,
          }),
          color: activeText,
        }
      : {
          bg: 'transparent',
          border: isAccent ? 'none' : '1px solid transparent',
          color: isDisabled
            ? tokens[colorScheme].disabledText
            : tokens[colorScheme].defaultText,
          ...(isInteractable && {
            _hover: {
              bg: tokens[colorScheme].segmentHoverBg,
              color: tokens[colorScheme].activeText,
            },
          }),
        }),
  }
}

export const segmentLabelStyles: SystemStyleObject = {
  fontFamily: "'Soehne Kraftig', sans-serif",
  fontStyle: 'normal',
  fontWeight: '500',
  fontSize: '14px',
  lineHeight: '20px',
  whiteSpace: 'nowrap',
  color: 'inherit',
}

export const spinnerStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  width: '12px',
  height: '12px',
  borderRadius: '50%',
  border: '1.5px solid',
  borderColor: tokens[colorScheme].spinnerBorder,
  borderTopColor: tokens[colorScheme].spinnerArc,
  flexShrink: 0,
})

/**
 * The error dot is the counterpart of the "new view ready" notification dot and deliberately
 * reuses its exact slot, so a segment transitioning ready → error shifts nothing.
 */
export const dotStyles = (
  colorScheme: ColorScheme = 'dark',
  tone: SegmentedControlDotTone = 'notification'
): SystemStyleObject => ({
  position: 'absolute',
  top: '3px',
  right: '3px',
  width: '6px',
  height: '6px',
  borderRadius: 'full',
  bg:
    tone === 'error'
      ? tokens[colorScheme].errorDot
      : tokens[colorScheme].notificationDot,
})
